import { ReactNode } from 'react'

interface ExternalLinkProps {
  href: string | null
  children: ReactNode
  className?: string
  variant?: 'primary' | 'secondary' | 'success' | 'warning' | 'error'
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode
  disabled?: boolean
}

export default function ExternalLink({
  href,
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  icon,
  disabled = false
}: ExternalLinkProps) {
  const baseClasses = 'inline-flex items-center rounded-lg font-medium transition-all duration-200 cursor-pointer group'

  const variantClasses = {
    primary: 'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 hover:shadow-md border border-blue-200 hover:border-blue-300',
    secondary: 'text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-gray-700 hover:shadow-md border border-gray-200 hover:border-gray-300',
    success: 'text-green-600 bg-green-50 hover:bg-green-100 hover:text-green-700 hover:shadow-md border border-green-200 hover:border-green-300',
    warning: 'text-orange-600 bg-orange-50 hover:bg-orange-100 hover:text-orange-700 hover:shadow-md border border-orange-200 hover:border-orange-300',
    error: 'text-red-600 bg-red-50 hover:bg-red-100 hover:text-red-700 hover:shadow-md border border-red-200 hover:border-red-300'
  }

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  const disabledClasses = 'opacity-50 cursor-not-allowed hover:shadow-none hover:scale-100'

  const naText = (
    <span className="ml-2 text-xs opacity-60 font-normal">N/A</span>
  )

  const defaultIcon = (
    <svg
      className={`ml-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 max-w-0 group-hover:max-w-[12px] overflow-hidden flex-shrink-0 self-center ${size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
    </svg>
  )

  if (!href || disabled) {
    return (
      <span className={`${baseClasses} ${variantClasses.secondary} ${sizeClasses[size]} ${disabledClasses} ${className}`}>
        {children}
        {icon || naText}
      </span>
    )
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className} break-all hover:scale-105 active:scale-95`}
      title={`Open ${href} in new window`}
    >
      {children}
      {icon || defaultIcon}
    </a>
  )
}